/* eslint-disable react/no-unstable-nested-components */
import { ColumnDef } from "@tanstack/react-table";
import * as React from "react";
import {
    Button,
    Card,
    DialogContent,
    Dialog,
    Table,
    Dropdown,
    DropdownTrigger,
    MoreButton,
    DropdownContent,
    DropdownItem,
} from "@jhool-io/fe-components";
import { useQueryClient } from "@tanstack/react-query";
import { useFetchNoteAccessOverrides } from "../../hooks/note-access-overrides.queries";
import { IExcludedProvider } from "../../types/note-access-overrides.types";
import Loader from "../../../../shared-ui/Loader/Loader";
import ListState from "../../../../components/ListState/ListState";
import AddNoteAccessOverrideForm from "../AddNoteAccessOverrideForm/AddNoteAccessOverrideForm";
import {
    displayNameInRightFormat,
    formatDate,
} from "../../../../utils/helpers";
import { getProviderCredenetialPillStyle } from "../../../providers/helpers/providers.helper";
import { useDoesUserHavePermission } from "../../../../hooks/permissions";
import { UserPermisions } from "../../../../utils/types/user";
import { useAddSettingsChanges } from "../../../practice-settings/hooks/mutations/practise-settings";
import useToast from "../../../../hooks/useToast";

export default function AccessOverridesList() {
    const [showAddDialog, setShowAddDialog] = React.useState(false);
    const { data, isLoading, error, isSuccess } = useFetchNoteAccessOverrides();

    const canAddNoteAccessOverride = useDoesUserHavePermission([
        UserPermisions.PROVIDER_STATUS_RULE_WRITE,
    ]);

    const addNewSettings = useAddSettingsChanges();

    const queryClient = useQueryClient();

    const { toast } = useToast();

    const canDeleteNoteAccessOverride = useDoesUserHavePermission([
        UserPermisions.PROVIDER_STATUS_RULE_WRITE,
    ]);

    const handleRemoveNoteAccessOverride = (providerId: string) => {
        const existingProviderIds =
            data?.data.providers.map(
                (provider) => provider.provider.provider_id
            ) ?? [];
        const providersToSend = JSON.stringify(
            existingProviderIds.filter((id) => id !== providerId)
        );

        addNewSettings.mutate(
            {
                setting_id: data?.data.setting_id ?? "",
                current_value: providersToSend,
            },
            {
                onSuccess: () => {
                    toast({
                        mode: "success",
                        message: "Note access override removed successfully",
                    });
                    queryClient.invalidateQueries({
                        queryKey: ["note-access-overrides"],
                    });
                },
                onError: (err) => {
                    toast({
                        mode: "error",
                        message:
                            err.response?.data.message ||
                            "Error removing note access override",
                    });
                },
            }
        );
    };

    const columns: ColumnDef<IExcludedProvider>[] = [
        {
            accessorKey: "provider_name",
            header: "PROVIDER NAME",
            cell: ({ row }) =>
                displayNameInRightFormat(row.original.provider.provider_name),
        },

        {
            accessorKey: "credential",
            header: "CREDENTIAL",
            cell: ({ row }) =>
                row.original.provider.credential
                    ? getProviderCredenetialPillStyle(
                          row.original.provider.credential
                      )
                    : "--",
        },
        {
            accessorKey: "created_by",
            header: "CREATED BY",
            cell: ({ row }) =>
                row.original.creator.first_name
                    ? displayNameInRightFormat({
                          firstName: row.original.creator.first_name,
                          lastName: row.original.creator.last_name,
                      })
                    : "--",
        },
        {
            accessorKey: "created_time",
            header: "CREATED AT",
            cell: ({ row }) =>
                row.original.created_time
                    ? formatDate(
                          row.original.created_time,
                          false,
                          "MM/dd/yyyy, hh:mmaaa"
                      )
                    : "--",
        },
        {
            accessorKey: "provider_id",
            header: "",
            cell: ({ row }) =>
                canDeleteNoteAccessOverride && (
                    <Dropdown>
                        <DropdownTrigger asChild>
                            <MoreButton />
                        </DropdownTrigger>
                        <DropdownContent width="auto" align="end">
                            <DropdownItem
                                onClick={() => {
                                    handleRemoveNoteAccessOverride(
                                        row.original.provider.provider_id
                                    );
                                }}
                            >
                                Delete
                            </DropdownItem>
                        </DropdownContent>
                    </Dropdown>
                ),
        },
    ];

    return (
        <div>
            <Dialog open={showAddDialog}>
                <DialogContent
                    title="Add provider"
                    saveText="Add"
                    variant="center"
                    showFooter
                    cancelText="Cancel"
                    submitBtnFormValue="add-note-access-override"
                    handleCloseDialog={() => setShowAddDialog(false)}
                >
                    <AddNoteAccessOverrideForm
                        onFormSubmit={() => setShowAddDialog(false)}
                    />
                </DialogContent>
            </Dialog>
            {canAddNoteAccessOverride && (
                <Card classNames="mb-[--spacing-header] gap-[--spacing-items] flex justify-end items-center flex-wrap">
                    <Button onClick={() => setShowAddDialog(true)}>
                        Add provider
                    </Button>
                </Card>
            )}
            {isLoading && <Loader />}
            {error && error?.response?.status !== 404 && (
                <ListState
                    isError
                    stateHelperText="Try reloading this page to solve this issue"
                    errorMsg="Cannot display note access overrides list at this time. Please try again later"
                />
            )}
            {data && isSuccess && data.data.providers.length === 0 && (
                <ListState
                    isError={false}
                    stateHelperText="Note access overrides will appear here once added"
                    emptyMessage="No note access overrides added yet"
                />
            )}
            {data && isSuccess && data.data.providers.length > 0 && (
                <Table columns={columns} data={data.data.providers} />
            )}
        </div>
    );
}
